/**
 * Date Calculation Utilities
 *
 * Helper functions for calculating date ranges, periods, and formatting.
 * These are used by contributor and team statistics calculations.
 */

export type TimeRange = "7days" | "30days" | "all"
export type PeriodUnit = "day" | "week" | "month"

export interface ChartPeriodConfig {
  periods: number
  unit: PeriodUnit
}

/**
 * Get yesterday's date at 23:59:59.999
 * This is used as the end date for all calculations to exclude the current incomplete day
 *
 * @returns Date representing yesterday at the end of the day
 */
export const getYesterday = (): Date => {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  yesterday.setHours(23, 59, 59, 999)
  return yesterday
}

/**
 * Get the start date for a time range
 * Note: Uses yesterday as the reference point to exclude the current incomplete day
 *
 * @param timeRange - The time range selection
 * @returns Date representing the start of the range
 */
export const getDateRangeStart = (timeRange: TimeRange): Date => {
  const yesterday = getYesterday()
  if (timeRange === "7days") {
    return new Date(yesterday.getTime() - 6 * 24 * 60 * 60 * 1000) // 7 days including yesterday
  }
  if (timeRange === "30days") {
    return new Date(yesterday.getTime() - 29 * 24 * 60 * 60 * 1000) // 30 days including yesterday
  }
  return new Date(0) // All time
}

/**
 * Determine chart granularity and period based on available data
 * Note: Uses yesterday as the reference point to exclude the current incomplete day
 *
 * @param timeRange - The time range selection
 * @param oldestDate - The oldest date in the data
 * @returns Configuration for chart periods
 */
export const getChartPeriod = (timeRange: TimeRange, oldestDate: Date): ChartPeriodConfig => {
  const yesterday = getYesterday()
  const totalDays = Math.floor((yesterday.getTime() - oldestDate.getTime()) / (24 * 60 * 60 * 1000))
  const totalWeeks = Math.floor(totalDays / 7)
  const totalMonths = Math.floor(totalDays / 30)

  if (timeRange === "7days") {
    return { periods: 7, unit: "day" }
  }

  if (timeRange === "30days") {
    // If >= 4 weeks available: last 4 weeks, else last 7 days
    if (totalWeeks >= 4) {
      return { periods: 4, unit: "week" }
    }
    return { periods: 7, unit: "day" }
  }

  // timeRange === "all"
  // If >= 3 months: last 6 months, else if >= 4 weeks: last 4 weeks, else last 7 days
  if (totalMonths >= 3) {
    return { periods: 6, unit: "month" }
  }
  if (totalWeeks >= 4) {
    return { periods: 4, unit: "week" }
  }
  return { periods: 7, unit: "day" }
}

/**
 * Format a date label based on the period unit
 *
 * @param date - The date to format
 * @param unit - The period unit
 * @returns Formatted date label string
 */
export const formatDateLabel = (date: Date, unit: PeriodUnit): string => {
  if (unit === "month") {
    return date.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    })
  }
  if (unit === "week") {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

/**
 * Get the start date for a specific period
 * Note: Uses yesterday as the reference point to exclude the current incomplete day
 *
 * @param periodIndex - The index of the period (0 = oldest)
 * @param totalPeriods - Total number of periods
 * @param unit - The period unit
 * @returns Date representing the start of that period
 */
export const getPeriodStart = (
  periodIndex: number,
  totalPeriods: number,
  unit: PeriodUnit,
): Date => {
  const yesterday = getYesterday()
  const periodOffset = totalPeriods - periodIndex - 1

  if (unit === "day") {
    return new Date(yesterday.getTime() - periodOffset * 24 * 60 * 60 * 1000)
  }
  if (unit === "week") {
    return new Date(yesterday.getTime() - periodOffset * 7 * 24 * 60 * 60 * 1000)
  }
  // month
  const date = new Date(yesterday)
  date.setMonth(date.getMonth() - periodOffset)
  return date
}

/**
 * Get the period index for a given date
 * Note: Uses yesterday as the reference point to exclude the current incomplete day
 *
 * @param date - The date to find the period for
 * @param periods - Total number of periods
 * @param unit - The period unit
 * @returns Period index (0 = oldest), or -1 if outside range
 */
export const getPeriodIndex = (date: Date, periods: number, unit: PeriodUnit): number => {
  const yesterday = getYesterday()
  let diff: number

  if (unit === "day") {
    diff = Math.floor((yesterday.getTime() - date.getTime()) / (24 * 60 * 60 * 1000))
  } else if (unit === "week") {
    diff = Math.floor((yesterday.getTime() - date.getTime()) / (7 * 24 * 60 * 60 * 1000))
  } else {
    // month
    const monthsDiff =
      (yesterday.getFullYear() - date.getFullYear()) * 12 + (yesterday.getMonth() - date.getMonth())
    diff = monthsDiff
  }

  const periodIndex = periods - diff - 1
  return periodIndex >= 0 && periodIndex < periods ? periodIndex : -1
}

/**
 * Calculate the number of days in a time range
 * Note: Uses yesterday as the reference point to exclude the current incomplete day
 *
 * @param timeRange - The time range selection
 * @param oldestCommitDate - The oldest commit date (for "all" range)
 * @param oldestPRDate - The oldest PR date (for "all" range)
 * @returns Number of days in the range (minimum 30)
 */
export const getTimeRangeDays = (
  timeRange: TimeRange,
  oldestCommitDate?: Date,
  oldestPRDate?: Date,
): number => {
  if (timeRange === "7days") return 7
  if (timeRange === "30days") return 30

  // For "all", calculate actual days from oldest data to yesterday
  const yesterday = getYesterday()
  const oldestDates: number[] = []

  if (oldestCommitDate) {
    oldestDates.push(oldestCommitDate.getTime())
  }
  if (oldestPRDate) {
    oldestDates.push(oldestPRDate.getTime())
  }

  if (oldestDates.length === 0) {
    return 30 // Default if no data
  }

  const oldestDate = new Date(Math.min(...oldestDates))
  return Math.max(
    30,
    Math.floor((yesterday.getTime() - oldestDate.getTime()) / (24 * 60 * 60 * 1000)),
  )
}

/**
 * Find the oldest date in a set of data
 *
 * @param commitDates - Array of commit dates
 * @param prDates - Array of PR dates
 * @returns The oldest date found, or current date if no dates provided
 */
export const getOldestDataDate = (commitDates: Date[], prDates: Date[]): Date => {
  const allDates = [...commitDates, ...prDates]

  if (allDates.length === 0) {
    return new Date()
  }

  return new Date(Math.min(...allDates.map((d) => d.getTime())))
}
